import React, { useState } from "react";
import styles from "./listing-status-badge.module.css";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import IconCheckCircle from "../icons/IconCheckCircle";
import IconError from "../icons/IconError";
import { Tooltip } from "../Tooltip/Tooltip";
import ListingErrorModal from "../ListingErrorModal/ListingErrorModal";
import { SellerProductData } from "@/contexts/InventoryContext";
import { getDifferenceInMinutes } from "@/utils/dateUtils";

type ListingStatusBadgeProps = {
  sellerData: SellerProductData;
  noIcons?: boolean;
  proccessingTooltipPosition: "left" | "right" | "bottom" | "top";
};

const ListingStatusBadge = ({
  sellerData,
  proccessingTooltipPosition = "left",
  noIcons = false,
}: ListingStatusBadgeProps) => {
  const [errorModalOpen, setErrorModalOpen] = useState<boolean>(false);
  const date = new Date();
  const minutesSinceSubmitted = getDifferenceInMinutes(sellerData.submittedAt, date.toISOString());
  let tooltipText = !!sellerData.strandedDate ? `The item was stranded on ${sellerData.strandedDate}. ` : "";
  if (!!sellerData.strandedRemoveDate) {
    if (tooltipText.length > 0) {
      tooltipText += `It will be automatically removed on ${sellerData.strandedRemoveDate}.`;
    }
    else {
      tooltipText += `The item will be automatically removed on ${sellerData.strandedRemoveDate}.`;
    }
  }

  return (
    <>
      <ListingErrorModal
        open={errorModalOpen}
        setOpen={(val: boolean) => setErrorModalOpen(val)}
        sellerData={sellerData}
      />
      {sellerData.status === "PROCESSING" ? (
        <Tooltip placement={proccessingTooltipPosition} title={`Processing may take up to 30 minutes. This listing has been processing for ${minutesSinceSubmitted} minutes.`}>
          <div className={`
            ${styles.statusContainer}
            ${styles.pending}
          `}>
            <LoadingSpinner className={styles.loader} size={"1rem"} />
            <div>Pending</div>
          </div>
        </Tooltip>
      ) : (
        <div
          className={`
            ${styles.statusContainer}
            ${sellerData.status === "LISTED" ? styles.listed : ""}
            ${sellerData.status === "ERROR" ? styles.error : ""}
          `}
          onClick={() => setErrorModalOpen(true)}
        >
          {sellerData.status === "LISTED" && (
            <>
              {!noIcons && <IconCheckCircle />}
              <div>Active</div>
            </>
          )}
          {sellerData.status === "ERROR" && !sellerData.strandedReason && (
            <>
              {!noIcons && <IconError />}
              <div>Error</div>
            </>
          )}
          {sellerData.status === "ERROR" && !!sellerData.strandedReason && (
            <>
              {!noIcons && <IconError />}
              {tooltipText.length > 0 &&
                <Tooltip placement={proccessingTooltipPosition} title={tooltipText}>
                  <div>{`Stranded (${sellerData.strandedReason})`}</div>
                </Tooltip> 
              }
              {
                tooltipText.length === 0 && 
                <div>{`Stranded (${sellerData.strandedReason})`}</div>
              }
            </>
          )}
          {sellerData.status === "INACTIVE" && (
            <div>Inactive</div>
          )}
          {sellerData.status === "INCOMPLETE" && (
            <div>Incomplete</div>
          )}
          {sellerData.status === "DRAFT" && (
            <div>Draft</div>
          )}
        </div>
      )}
    </>
  );
};

export default ListingStatusBadge;